import React from "react"
import "../App.css"
import PageTitle from "../components/subcomponents/PageTitle";
import {LMTableApi, PaginationModel, SortModel} from "../components/Tables/types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";

import CircularProgress from '@mui/material/CircularProgress';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TableFactory from "../components/TableFactory";
import {TablePaper} from "../components/TablePaper";
import CustomTabPanel from "../components/CustomTablePanel";
import ErrorExclamation from "../components/subcomponents/ErrorExclamation";
import TableSearch from "../components/subcomponents/TableSearch";
import {Product} from "../models/product";
import AddButton from "../components/subcomponents/Buttons/AddButton";
import DownloadIcon from '@mui/icons-material/Download';
import ButtonWithMargin from "../components/subcomponents/Buttons/ButtonWithMargin";
import {ButtonContainer} from "../components/subcomponents/Containers/Containers";
import {useNavigate} from "react-router-dom";
import InCareNetHFService from "../services/InCareNetHFService";
import {activeFieldNames, inActiveFieldNames, Products} from "../constants";
import RefreshIcon from '@mui/icons-material/Refresh';
import {green} from "@mui/material/colors";
import {loadRefreshCountDate, postRefreshCounts, getRefreshStatus, DeviceRefreshRun} from "../api/software";
import {classifyRefreshRunSnapshot} from "../utils/pollingLogic";
import {useTranslation} from "react-i18next";
import {DataGridCsvExporter} from "../export/csv/DataGridCSVExporter";
import {applySearchFilter, timeStamp} from "../utils/helperFunctions";
import {CUSTOMER_NUMBER_SORT_MODEL} from "../utils/customerNumberSort";
import ImportExportService from "../services/ImportExportServiceFunctions";
import {useUserRole} from "../utils/UserRoleContext";
import {logBusinessAudit} from "../utils/auditLogHelper";

interface ProductPageProps {
  productType: string;
}

export const ProductPage: React.FC<ProductPageProps> = ({productType}) => {

  const navigate = useNavigate()

  const [loadingTables, setLoadingTables] = React.useState(false)
  const [tabValue, setTabValue] = React.useState(0)
  const [isWSRefreshing, setIsWSRefreshing] = React.useState(false)
  const [wsCompleted, setWsCompleted] = React.useState(0)
  const [wsTotal, setWsTotal] = React.useState(0)
  // The run this page is following; completion handling fires exactly once,
  // when a poll sees the watched run leave 'running'.
  const watchedRunIdRef = React.useRef<string | null>(null)
  const [refreshingCounts, setRefreshingCounts] = React.useState(false)
  const [refreshCountDate, setRefreshCountDate] = React.useState('')
  const [items, setItems] = React.useState<any[]>([])
  const [error, setError] = React.useState(false)
  const [selectedRowIds, setSelectedRowIds] = React.useState<string[]>([]);
  const [selectedDevCountGroup, setSelectedDevCountGroup] = React.useState<string>('active');
  const {isAdmin} = useUserRole();

  const [searchFilter, setSearchFilter] = React.useState('')
  const [selectedDate, setSelectedDate] = React.useState(dayjs())
  const [failedDialogOpen, setFailedDialogOpen] = React.useState(false)
  const [failedCustomerIds, setFailedCustomerIds] = React.useState<string[]>([])

  const [pagination, setPagination] = React.useState<PaginationModel>({page: 0, pageSize: 15});
  const [sortModel, setSortModel] = React.useState<SortModel>(CUSTOMER_NUMBER_SORT_MODEL);

  const {t} = useTranslation()

  React.useEffect(() => {
    loadData().then()
  }, [tabValue, selectedDate])

  // Latest-render snapshot handler; the poll interval calls through this ref
  // so its callbacks always see fresh state (no stale closures). The
  // decision logic lives in classifyRefreshRunSnapshot (pure, unit-tested);
  // this only applies the resulting action to page state. The completion
  // business audit is written server-side at the run's completion point
  // (DeviceRefreshService) — exactly once per run, cron runs included — so
  // watching pages are purely presentational here.
  const applyRunSnapshotRef = React.useRef<(run: DeviceRefreshRun | null) => void>(() => {})
  applyRunSnapshotRef.current = (run: DeviceRefreshRun | null) => {
    const action = classifyRefreshRunSnapshot(run, watchedRunIdRef.current)
    switch (action.kind) {
      case 'follow':
        watchedRunIdRef.current = action.runId
        setIsWSRefreshing(true)
        setWsCompleted(action.completed)
        setWsTotal(action.total)
        break
      case 'stop-idle':
        // A finished run this page never followed — just make sure the
        // spinner isn't stuck (e.g. a 409 raced a run that ended in between).
        setIsWSRefreshing(false)
        break
      case 'complete':
        // The run this page was following has finished.
        watchedRunIdRef.current = null
        setIsWSRefreshing(false)
        loadData().catch((e) => console.error('Failed to reload data after refresh:', e))
        if (action.failed) {
          alert(t('objects.products.refresh.refreshNotSuccessful'))
        } else if (action.failedCustomerIds.length > 0) {
          setFailedCustomerIds(action.failedCustomerIds)
          setFailedDialogOpen(true)
        }
        break
      default:
        break
    }
  }

  // On mount: pick up a refresh already running anywhere in the cluster
  // (replaces the socket's on-connect 'refresh-status' snapshot — and unlike
  // it, survives page reloads and works from any backend replica).
  React.useEffect(() => {
    if (productType !== 'inCareNetHF') return
    getRefreshStatus()
      .then((run) => applyRunSnapshotRef.current(run))
      .catch((e) => console.error('Failed to fetch refresh status:', e))
  }, [productType])

  // While a run is live, poll its progress.
  React.useEffect(() => {
    if (productType !== 'inCareNetHF' || !isWSRefreshing) return
    const interval = setInterval(() => {
      getRefreshStatus()
        .then((run) => applyRunSnapshotRef.current(run))
        .catch((e) => console.error('Failed to poll refresh status:', e))
    }, 2500)
    return () => clearInterval(interval)
  }, [productType, isWSRefreshing])

  async function loadData() {
    const selectedProduct = Products[tabValue]
    setLoadingTables(true)
    try {
      // If the product is inCareNetHF and a date is selected, pass the date to getTableItems
      if (selectedProduct.name === 'inCareNetHF' && selectedDate) {
        setLoadingTables(true)

        if (selectedDate.isSame(dayjs(), 'day')) {
          const request = await selectedProduct.getTableItems();
          setItems(request)
        } else {
          const request = await selectedProduct.getTableItems(selectedDate.format('YYYY-MM-DD'));
          setItems(request)
        }
      } else {
        setItems(await selectedProduct.getTableItems())
      }
      setError(false)
    } catch (e) {
      console.error(e)
      setError(true)
    }

    const time = await loadRefreshCountDate()
    setRefreshCountDate(time.lastDeviceCountUpdate)
    setLoadingTables(false)
  }

  function handleTabChange(event: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue);
    navigate(`/products/${Products[newValue].name}`)
  }

  function renderTab(label: string, id: string) {
    return (<Tab label={label} id={id} key={id}/>)
  }

  function handleSelectionChange(selectionModel: any) {
    if (!selectionModel.length) {
      setSelectedRowIds([]);
      return;
    }

    const lastId = selectionModel[selectionModel.length - 1];
    setSelectedRowIds([lastId]);
  }

  function getSelectedProductObject() {
    if (!selectedRowIds.length) return undefined;
    return items.find((elem) => elem.id === selectedRowIds[0])
  }


  function handleAddClick() {
    navigate("/products/add")
  }

  async function handleUpdate() {
    const selectedProduct = getSelectedProductObject();
    if (!selectedProduct) return;

    navigate(`/customers/update/${selectedProduct.customerId}?fallbackURL=products/${productType}&product=${selectedProduct.id}`)
  }

  async function handleDelete() {
    if (selectedRowIds.length !== 1) return;

    const deleteOperation = Products[tabValue].deleteItem;
    const idToDelete = selectedRowIds[0];

    try {
      const deletedProduct = items.find(p => p.id === idToDelete)
      await deleteOperation(idToDelete);
      await logBusinessAudit('audit.product.deleted', '', deletedProduct ?? null, null)

      setSelectedRowIds([]);

      await loadData();
    } catch (e) {
      console.error(e);
      setError(true);
    }
  }


  function isUpdateButtonDisabled() {
    return selectedRowIds.length !== 1
  }

  function handleDevCountGroupChange(event: SelectChangeEvent) {
    setSelectedDevCountGroup(event.target.value);
  }

  function filterColumnsByDevCountGroup(columnsNames: string[]) {
    return selectedDevCountGroup === 'active' ?
      columnsNames.filter(elem => !inActiveFieldNames.includes(elem))
      : columnsNames.filter(elem => !activeFieldNames.includes(elem))
  }

  function createCustomTabPanel(product: Product, index: number) {
    // to prevent rendering all products at the same time
    if (tabValue !== index)
      return <div/>

    if (error) {
      return <ErrorExclamation message={t('objects.products.failedFetchingData')}/>
    }

    const filteredItems = applySearchFilter(items, searchFilter)

    if (loadingTables) {
      return <div><CircularProgress size={100}/></div>
    }

    let innerElement;

    if (productType === 'inCareNetHF') {
      innerElement = renderInCareNetHFTabPanel(product, filteredItems)
    } else {
      innerElement = TableFactory.createTable(
        product.name,
        product.tableColumns.map(elem => elem.field),
        filteredItems,
        selectedRowIds,
        handleSelectionChange,
        (model) => {
          setSortModel(model);
        },
        {sortModel},
        apiRef,
        pagination,
        setPagination,
        filteredItems.length,
        loadingTables,
        true
      );
    }

    return (
      <CustomTabPanel value={tabValue} index={index} key={product.name}>
        {innerElement}
      </CustomTabPanel>
    )
  }

  const apiRef = React.useRef<LMTableApi | null>(null);

  function renderInCareNetHFTabPanel(product: Product, filteredItems: any[]) {
    function exportCSV() {
      if (!apiRef.current) return;
      const columns = apiRef.current.getVisibleColumns()

      // getSortedRows(), not the unsorted filteredItems: this table is handed every
      // filtered row (showFullContent), so the two hold the same customers — but only
      // the table's copy is in the order the user is looking at.
      const rows = apiRef.current.getSortedRows()

      const exporter = new DataGridCsvExporter(rows, columns, {
        filename: `${product.name} Tabellen Export ${timeStamp()}`,
        delimiter: ";",
        includeHeaders: true,
      });

      exporter.exportToCsv();
    }

    return (
      <div>
        <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
              marginTop: 0,
              paddingTop: 0,
              paddingLeft: 2,
              paddingRight: 2,
              flexFlow: 'row wrap'
            }}>
          <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)', marginTop: 1}}>
            <div style={{display: 'flex', justifyContent: 'left'}}>
              <Box sx={{
                width: "100%",
                textAlign: "left",
                marginBottom: "1rem"
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                  <Select
                    value={selectedDevCountGroup}
                    label={"Gerätezahl-Gruppe"}
                    onChange={handleDevCountGroupChange}
                  >
                    <MenuItem key={"active"} value={"active"}>{t('objects.products.activeDeviceCounts')}</MenuItem>
                    <MenuItem key={"inactive"}
                              value={"inactive"}>{t('objects.products.inActiveDeviceCounts')}</MenuItem>
                  </Select>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Datum auswählen"
                      disableFuture
                      format={'DD/MM/YYYY'}
                      value={selectedDate}
                      onChange={(newValue) => setSelectedDate(newValue || dayjs())}
                      slotProps={{textField: {size: 'small'}}}
                    />
                  </LocalizationProvider>
                </div>
              </Box>
            </div>
          </Box>

          <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)', marginTop: 1}}>
            <div style={{display: 'flex', justifyContent: 'right', alignItems: 'center'}}>
              <Button
                variant="contained"
                disabled={!isAdmin}
                onClick={exportCSV}
                style={{marginRight: "2rem"}}
                startIcon={<DownloadIcon/>}
              >CSV</Button>
              <Button
                variant="contained"
                disabled={!isAdmin}
                style={{marginRight: "2rem"}}
                onClick={async () => {
                  if (!apiRef.current) return;
                  setRefreshingCounts(true)

                  await ImportExportService.downloadExcel(selectedDate.format('YYYY-MM-DD'), sortModel)
                  setRefreshingCounts(false)
                }}
                startIcon={<DownloadIcon/>}
              >Excel</Button>


              <Box sx={{position: 'relative'}}>
                <Tooltip title={'Last refreshed on: ' + refreshCountDate} arrow>
                  <Fab
                    size={'small'}
                    sx={isWSRefreshing || refreshingCounts ? {
                      animation: "spin .85s ease-in infinite",
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "100%": {
                          transform: "rotate(360deg)",
                        },
                      },
                    } : {}}
                    disabled={isWSRefreshing || refreshingCounts}
                    color={'primary'}
                    onClick={async () => {
                      setWsCompleted(0)
                      setWsTotal(0)
                      setIsWSRefreshing(true)
                      try {
                        const result = await postRefreshCounts()
                        if (result.run) applyRunSnapshotRef.current(result.run)
                        if (!result.started) {
                          // A run started elsewhere — follow its progress, but
                          // tell only this requester (no global broadcast).
                          alert(`Ein Refresh läuft bereits.\nFortschritt: ${result.run?.completed ?? 0} / ${result.run?.total ?? 0} Instanzen`)
                        }
                      } catch (e) {
                        setIsWSRefreshing(false)
                        console.error('Failed to trigger refresh:', e)
                        alert(t('objects.products.refresh.refreshNotSuccessful'))
                      }
                    }}
                    variant={'circular'}>
                    <RefreshIcon/>
                  </Fab>
                </Tooltip>

                {
                  (isWSRefreshing || refreshingCounts) && (
                    <CircularProgress
                      size={'small'}
                      sx={{
                        color: green[500],
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        zIndex: 1,
                      }}
                    />
                  )
                }
              </Box>
            </div>
          </Box>
        </Box>
        {isWSRefreshing && (
          <Box sx={{width: '100%', px: 2, py: 1}}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Gerätezahlen werden aktualisiert…{wsTotal > 0 ? ` ${wsCompleted} / ${wsTotal} Instanzen` : ''}
            </Typography>
            <LinearProgress/>
          </Box>
        )}
        {TableFactory.createTable(
            product.name,
            filterColumnsByDevCountGroup(product.tableColumns.map(elem => elem.field)),
            filteredItems,
            selectedRowIds,
            handleSelectionChange,
            (model) => {
              setSortModel(model);
            },
            {
              sortModel,
              summaryFooter: true
            },
            apiRef,
            pagination,
            setPagination,
            filteredItems.length,
            loadingTables,
            true
          )}
      </div>
    )
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('appBar.products')}</PageTitle>
      <Divider/>
      <TablePaper>
        <Box>
          <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
                marginTop: 0,
                paddingTop: 0,
                paddingLeft: 2,
                paddingRight: 2,
                flexFlow: 'row wrap'
              }}>

            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'left'}}>
                <TableSearch onSearch={(input) => {
                  setSearchFilter(input)
                }}/>
              </div>
            </Box>

            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'right'}}>
                <AddButton onClick={handleAddClick} disabled={!isAdmin}/>
              </div>
            </Box>
          </Box>


          <Box>
            <Tabs value={tabValue} onChange={handleTabChange}>
              {Products.map((product, index) => renderTab(product.label, `tab-${index}`))}
            </Tabs>
          </Box>

          {Products.map((elem, index) => createCustomTabPanel(elem, index))}

          <ButtonContainer>
            <ButtonWithMargin
              variant="contained"
              color="secondary"
              disabled={isUpdateButtonDisabled() || !isAdmin}
              onClick={handleDelete}
            >
              {t('activities.delete')}
            </ButtonWithMargin>
            <Button
              variant="contained"
              disabled={isUpdateButtonDisabled()}
              onClick={handleUpdate}
            >
              {isAdmin ? t('activities.edit') : t('activities.view')}
            </Button>
          </ButtonContainer>
        </Box>
      </TablePaper>

      <Dialog
        open={failedDialogOpen}
        onClose={() => setFailedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('objects.products.refresh.failedTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            {t('objects.products.refresh.failedDescription')}
          </Typography>
          <List dense>
            {failedCustomerIds.map((id) => (
              <ListItem key={id}>
                <ListItemText primary={id}/>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailedDialogOpen(false)}>{t('objects.products.refresh.closeButton')}</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
